import { useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Description,
  FieldError,
  Form,
  Input,
  Label,
  Switch,
  TextField,
} from "@heroui/react";

import { AppVersions, PLATFORMS, VERSION_PATTERN } from "../types";
import { useUpdateAppVersions } from "../use-app-versions";

import { dayjs, dt } from "@/shared/helper/datetime.ts";

// datetime-local работает со строкой без таймзоны, а сервер ждёт RFC3339.
// Оба конца приводим к таймзоне Алматы, чтобы «до 18:00» значило 18:00 в Алматы
// независимо от того, где сидит админ.
const DATETIME_LOCAL = "YYYY-MM-DDTHH:mm";

const toLocalInput = (value: string | null) =>
  value ? dt(value).format(DATETIME_LOCAL) : "";

const fromLocalInput = (value: string) =>
  value ? dayjs.tz(value, "Asia/Almaty").toISOString() : null;

export function AppVersionsForm({ versions }: { versions: AppVersions }) {
  const { mutateAsync, isPending } = useUpdateAppVersions();

  const [savedAt, setSavedAt] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<AppVersions>({
    defaultValues: {
      ...versions,
      maintenanceUntil: toLocalInput(versions.maintenanceUntil),
    },
  });

  const isMaintenance = useWatch({ control, name: "isMaintenance" });

  const onSubmit: SubmitHandler<AppVersions> = (values) =>
    mutateAsync({
      ...values,
      maintenanceUntil: fromLocalInput(values.maintenanceUntil ?? ""),
    }).then((response) => {
      reset({
        ...(response.data ?? values),
        maintenanceUntil: toLocalInput(response.data?.maintenanceUntil ?? null),
      });
      setSavedAt(new Date().toLocaleTimeString("ru-RU"));
    });

  const versionField = (
    name: keyof AppVersions,
    label: string,
    hint?: string,
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ fieldState, field }) => (
        <TextField
          isInvalid={!!fieldState.error}
          value={String(field.value ?? "")}
          onBlur={field.onBlur}
          onChange={field.onChange}
        >
          <Label>{label}</Label>
          <Input ref={field.ref} placeholder="1.0.13" />
          {hint && <Description>{hint}</Description>}
          <FieldError>{fieldState.error?.message}</FieldError>
        </TextField>
      )}
      rules={{
        required: "Нужно заполнить поле",
        pattern: {
          value: VERSION_PATTERN,
          message: "Формат: 1.0.13",
        },
      }}
    />
  );

  return (
    <Form className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
      {PLATFORMS.map((platform) => (
        <div key={platform.key} className="flex flex-col gap-4">
          <p className="font-bold">{platform.title}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {versionField(platform.versionField, "Актуальная версия")}
            {versionField(
              platform.minimalField,
              "Минимальная версия",
              "Сборки ниже считаются устаревшими",
            )}

            <Controller
              control={control}
              name={platform.urlField}
              render={({ fieldState, field }) => (
                <TextField
                  isInvalid={!!fieldState.error}
                  value={String(field.value ?? "")}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                >
                  <Label>Ссылка на обновление</Label>
                  <Input
                    ref={field.ref}
                    placeholder={platform.urlPlaceholder}
                  />
                  <FieldError>{fieldState.error?.message}</FieldError>
                </TextField>
              )}
              rules={{ required: "Нужно заполнить поле" }}
            />
          </div>
        </div>
      ))}

      <div className="flex flex-col gap-4">
        <p className="font-bold">Шторка обновления</p>

        <Controller
          control={control}
          name="isShowUpdate"
          render={({ field }) => (
            <Switch isSelected={!!field.value} onChange={field.onChange}>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <div className="flex flex-col">
                <span>Показывать всем</span>
                <span className="text-sm text-default-500">
                  Мягкая шторка: пользователь может её закрыть
                </span>
              </div>
            </Switch>
          )}
        />

        <Controller
          control={control}
          name="isRequiredUpdate"
          render={({ field }) => (
            <Switch isSelected={!!field.value} onChange={field.onChange}>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <div className="flex flex-col">
                <span>Требовать обновление ниже минимальной версии</span>
                <span className="text-sm text-default-500">
                  Такую шторку закрыть нельзя — работать в приложении не выйдет
                </span>
              </div>
            </Switch>
          )}
        />
      </div>

      <div className="flex flex-col gap-4">
        <p className="font-bold">Технические работы</p>

        <Controller
          control={control}
          name="isMaintenance"
          render={({ field }) => (
            <Switch isSelected={!!field.value} onChange={field.onChange}>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <div className="flex flex-col">
                <span>Идут технические работы</span>
                <span className="text-sm text-default-500">
                  Приложение показывает заглушку вместо экранов
                </span>
              </div>
            </Switch>
          )}
        />

        {isMaintenance && (
          <Controller
            control={control}
            name="maintenanceUntil"
            render={({ fieldState, field }) => (
              <TextField
                className="max-w-64"
                isInvalid={!!fieldState.error}
                value={String(field.value ?? "")}
                onBlur={field.onBlur}
                onChange={field.onChange}
              >
                <Label>До какого времени (Алматы)</Label>
                <Input ref={field.ref} type="datetime-local" />
                <Description>
                  Пусто — без срока. После этого времени заглушка пропадёт сама
                </Description>
                <FieldError>{fieldState.error?.message}</FieldError>
              </TextField>
            )}
          />
        )}
      </div>

      <div className="flex flex-row items-center gap-3">
        <Button
          isDisabled={!isDirty}
          isPending={isPending}
          type="submit"
          variant="primary"
        >
          Сохранить
        </Button>

        {savedAt && !isDirty && (
          <span className="text-sm text-default-500">
            Сохранено в {savedAt}
          </span>
        )}
      </div>
    </Form>
  );
}
